// src/app/tools/random-yaml/page.tsx
'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Copy, RefreshCw, Check, FileText, Plus, Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomYamlGeneratorContent } from '../../data/random-yaml-generator-content';
import Link from 'next/link';

type YamlValue = string | number | boolean | null | YamlValue[] | { [key: string]: YamlValue };

interface YamlField {
    key: string;
    type: 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array';
    children?: YamlField[];
    min?: number;
    max?: number;
    items?: number;
}

const DEFAULT_FIELDS: YamlField[] = [
    { key: 'id', type: 'number' },
    { key: 'name', type: 'string' },
    { key: 'email', type: 'string' },
    { key: 'active', type: 'boolean' },
    { key: 'tags', type: 'array', items: 3, children: [{ key: 'tag', type: 'string' }] }
];

const STRING_PREFIXES = [
    'user', 'item', 'product', 'order', 'customer', 'address', 'city', 'country',
    'description', 'title', 'name', 'first_name', 'last_name', 'email', 'phone'
];

const STRING_SUFFIXES = [
    'name', 'title', 'description', 'address', 'city', 'country', 'email', 'phone',
    'id', 'code', 'number', 'value', 'status', 'type'
];

const DOMAINS = ['example.com', 'test.com', 'demo.com', 'domain.com', 'sample.org'];

function generateRandomString(prefix = '') {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = prefix;

    if (!prefix) {
        const randomPrefix = STRING_PREFIXES[Math.floor(Math.random() * STRING_PREFIXES.length)];
        result = randomPrefix + '_';
    }

    const length = 3 + Math.floor(Math.random() * 5);
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    if (Math.random() > 0.5) {
        result += '_' + STRING_SUFFIXES[Math.floor(Math.random() * STRING_SUFFIXES.length)];
    }

    return result;
}

function generateRandomEmail() {
    const username = generateRandomString('user');
    const domain = DOMAINS[Math.floor(Math.random() * DOMAINS.length)];
    return `${username}@${domain}`;
}

function generateRandomValue(field: YamlField, depth = 0): YamlValue {
    switch (field.type) {
        case 'string':
            if (field.key.toLowerCase().includes('email')) {
                return generateRandomEmail();
            }
            return generateRandomString(field.key);
        case 'number':
            const min = field.min || 0;
            const max = field.max || 1000;
            return Math.floor(min + Math.random() * (max - min + 1));
        case 'boolean':
            return Math.random() > 0.5;
        case 'null':
            return null;
        case 'object':
            const obj: { [key: string]: YamlValue } = {};
            (field.children || []).forEach(childField => {
                obj[childField.key] = generateRandomValue(childField, depth + 1);
            });
            return obj;
        case 'array':
            const count = field.items || 3;
            return Array.from({ length: count }, () => {
                if (field.children?.length) {
                    const itemField = { ...field.children[0], key: 'item' };
                    return generateRandomValue(itemField, depth + 1);
                }
                return generateRandomValue({ ...field, type: 'string' }, depth + 1);
            });
        default:
            return '';
    }
}

function yamlStringify(obj: any, indent = 0): string {
    const indentStr = '  '.repeat(indent);

    if (Array.isArray(obj)) {
        if (obj.length === 0) return '[]';
        if (obj.every(item => typeof item !== 'object' || item === null)) {
            return `[${obj.map(item =>
                typeof item === 'string' ? `"${item.replace(/"/g, '\\"')}"` : item
            ).join(', ')}]`;
        }
        return obj.map(item =>
            `${indentStr}- ${yamlStringify(item, indent + 1).trimStart()}`
        ).join('\n');
    } else if (obj && typeof obj === 'object') {
        if (Object.keys(obj).length === 0) return '{}';
        return Object.entries(obj).map(([key, value]) => {
            const formattedKey = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? key : `"${key}"`;

            if (Array.isArray(value)) {
                return `${indentStr}${formattedKey}:\n${yamlStringify(value, indent + 1)}`;
            } else if (value && typeof value === 'object') {
                return `${indentStr}${formattedKey}:\n${yamlStringify(value, indent + 1)}`;
            } else {
                const formattedValue = typeof value === 'string'
                    ? `"${value.replace(/"/g, '\\"')}"`
                    : value === null ? 'null' : value;
                return `${indentStr}${formattedKey}: ${formattedValue}`;
            }
        }).join('\n');
    }
    return String(obj);
}

function generateYaml(fields: YamlField[]): string {
    const result: { [key: string]: any } = {};
    fields.forEach(field => {
        result[field.key] = generateRandomValue(field, 1);
    });
    return yamlStringify(result);
}

function YamlFieldEditor({
    field,
    onChange,
    onDelete,
    onAddChild,
    depth = 0
}: {
    field: YamlField;
    onChange: (field: YamlField) => void;
    onDelete: () => void;
    onAddChild: () => void;
    depth?: number;
}) {
    const [expanded, setExpanded] = useState(true);

    const toggleExpand = () => {
        setExpanded(!expanded);
    };

    return (
        <div className="mb-2 ml-4" style={{ marginLeft: `${depth * 1}rem` }}>
            <div className="flex items-center gap-2">
                {(field.type === 'object' || field.type === 'array') && (
                    <button
                        onClick={toggleExpand}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        {expanded ? '▼' : '▶'}
                    </button>
                )}
                <div className="flex-1 flex items-center gap-2">
                    <Input
                        placeholder="Field name"
                        value={field.key}
                        onChange={(e) => onChange({ ...field, key: e.target.value })}
                        className="flex-1 max-w-[200px]"
                    />
                    <select
                        value={field.type}
                        onChange={(e) => onChange({
                            ...field,
                            type: e.target.value as YamlField['type'],
                            ...(e.target.value !== 'array' && e.target.value !== 'object' ? { children: undefined } : {})
                        })}
                        className="w-32 px-2 py-1.5 border rounded-md text-sm"
                    >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                        <option value="null">Null</option>
                        <option value="object">Object</option>
                        <option value="array">Array</option>
                    </select>

                    {field.type === 'number' && (
                        <div className="flex gap-1">
                            <Input
                                type="number"
                                placeholder="Min"
                                value={field.min ?? ''}
                                onChange={(e) => onChange({
                                    ...field,
                                    min: e.target.value ? Number(e.target.value) : undefined
                                })}
                                className="w-16 text-sm h-8"
                            />
                            <Input
                                type="number"
                                placeholder="Max"
                                value={field.max ?? ''}
                                onChange={(e) => onChange({
                                    ...field,
                                    max: e.target.value ? Number(e.target.value) : undefined
                                })}
                                className="w-16 text-sm h-8"
                            />
                        </div>
                    )}

                    {field.type === 'array' && (
                        <Input
                            type="number"
                            placeholder="Items"
                            value={field.items ?? 3}
                            min="1"
                            max="20"
                            onChange={(e) => onChange({
                                ...field,
                                items: Math.min(20, Math.max(1, parseInt(e.target.value) || 1))
                            })}
                            className="w-20 text-sm h-8"
                        />
                    )}

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>

                    {(field.type === 'object' || field.type === 'array') && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onAddChild}
                            className="h-8 w-8"
                        >
                            <Plus className="h-3.5 w-3.5" />
                        </Button>
                    )}
                </div>
            </div>

            {expanded && field.children?.map((child, index) => (
                <YamlFieldEditor
                    key={index}
                    field={child}
                    onChange={(updatedChild) => {
                        const newChildren = [...(field.children || [])];
                        newChildren[index] = updatedChild;
                        onChange({ ...field, children: newChildren });
                    }}
                    onDelete={() => {
                        const newChildren = [...(field.children || [])];
                        newChildren.splice(index, 1);
                        onChange({ ...field, children: newChildren });
                    }}
                    onAddChild={() => {
                        const newChildren = [...(field.children || []), {
                            key: `field_${(field.children?.length || 0) + 1}`,
                            type: 'string' as const,
                            children: [],
                        }];
                        onChange({ ...field, children: newChildren });
                    }}
                    depth={depth + 1}
                />
            ))}
        </div>
    );
}

export default function RandomYamlGenerator() {
    const [fields, setFields] = useState<YamlField[]>(DEFAULT_FIELDS);
    const [yamlData, setYamlData] = useState<string>('');
    const [copied, setCopied] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const { toast } = useToast();

    // Debounce YAML generation
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const generateYamlData = useCallback((fieldsToGenerate: YamlField[]): void => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            setIsGenerating(true);
            try {
                const result = generateYaml(fieldsToGenerate);
                setYamlData(result);
            } catch (error) {
                console.error('Error generating YAML:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to generate YAML data. Please check your field configurations.',
                    variant: 'destructive',
                });
            } finally {
                setIsGenerating(false);
            }
        }, 300); // 300ms debounce delay
    }, [fields, toast]);

    // Initial generation on mount
    useEffect(() => {
        generateYamlData(fields);

        // Cleanup function
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    const handleAddField = () => {
        const newFields = [...fields, { key: `field_${fields.length + 1}`, type: 'string' as const }];
        setFields(newFields);
        generateYamlData(newFields);
    };

    const handleFieldChange = (index: number, updatedField: YamlField) => {
        const newFields = [...fields];
        newFields[index] = updatedField;
        setFields(newFields);
        generateYamlData(newFields);
    };

    const handleDeleteField = (index: number) => {
        if (fields.length > 1) {
            const newFields = [...fields];
            newFields.splice(index, 1);
            setFields(newFields);
            generateYamlData(newFields);
        }
    };

    const handleAddChild = (parentIndex: number) => {
        const newFields = [...fields];
        const parent = newFields[parentIndex];

        if (!parent.children) {
            parent.children = [];
        }

        parent.children.push({
            key: `field_${parent.children.length + 1}`,
            type: 'string' as const
        });

        setFields(newFields);
        generateYamlData(newFields);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(yamlData);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({
            title: 'Copied!',
            description: 'YAML data has been copied to clipboard.',
        });
    };

    const handleDownload = () => {
        const blob = new Blob([yamlData], { type: 'text/yaml;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'random_data.yaml';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast({
            title: 'Download started',
            description: 'Your YAML file is being downloaded.',
        });
    };

    const handleRegenerate = () => {
        generateYamlData(fields);
        toast({
            title: 'Success',
            description: 'YAML data has been regenerated.',
        });
    };

    return (
        <div className="container mx-auto py-8">
            <div className="grid gap-6">
                <ToolPageIntro title="Random YAML Generator">
                Generate random YAML data with our free online tool. Create test configurations, mock settings files, and sample YAML structures for development and testing.
            </ToolPageIntro>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>YAML Configuration</CardTitle>
                            <CardDescription>
                                Define the structure of your YAML data
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <Label>Fields</Label>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddField}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Field
                                        </Button>
                                    </div>
                                    <div className="space-y-2 border rounded-md p-4 bg-muted/20">
                                        {fields.map((field, index) => (
                                            <YamlFieldEditor
                                                key={index}
                                                field={field}
                                                onChange={(updatedField) => handleFieldChange(index, updatedField)}
                                                onDelete={() => handleDeleteField(index)}
                                                onAddChild={() => handleAddChild(index)}
                                                depth={0}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter className="flex justify-between">
                            <Button
                                variant="outline"
                                onClick={() => setFields(DEFAULT_FIELDS)}
                            >
                                Reset to Default
                            </Button>
                            <Button
                                onClick={handleRegenerate}
                                disabled={isGenerating}
                            >
                                {isGenerating ? (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Regenerate
                                    </>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>

                    <Card className="h-full flex flex-col">
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>YAML Output</CardTitle>
                                    <CardDescription>
                                        {fields.length} field{fields.length !== 1 ? 's' : ''}
                                    </CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!yamlData}
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4 mr-1 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4 mr-1" />
                                        )}
                                        {copied ? 'Copied!' : 'Copy'}
                                    </Button>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!yamlData}
                                    >
                                        <FileText className="h-4 w-4 mr-1" />
                                        Download YAML
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-hidden">
                            <div className="relative h-full">
                                <Textarea
                                    value={yamlData}
                                    readOnly
                                    className="h-full min-h-[300px] font-mono text-sm"
                                    placeholder="Your YAML data will appear here..."
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomYamlGeneratorContent.title}
                description={randomYamlGeneratorContent.description}
                content={randomYamlGeneratorContent.content}
                faqs={randomYamlGeneratorContent.faqs}
                relatedTools={randomYamlGeneratorContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}